/**
 * The viewer's access changed (E1.5 PR 4b, #1336): what open panels close on.
 *
 * A change is counted by `useAccessMe` when a re-read `/access/me` answer
 * differs from the previous one in what the viewer may see (policy, role,
 * owner flag) — NOT when the organization-wide access revision grows: that
 * grows for anyone's change in the organization, and closing every user's
 * open record on a colleague's new grant would be noise. A growth with the
 * viewer's access unchanged only refreshes data in the background.
 *
 * - `useAccessChange(onChange)` — runs `onChange` after a change: popovers,
 *   the report panel, the record card close; reopening asks the API afresh
 *   (A11: a kept id is not an authority).
 * - `useStaleAfterAccessChange(selection)` — the same fact derived IN RENDER
 *   for a selection that drives fetches: true from the very render the change
 *   lands in, so no child fetches the old selection before an effect could
 *   clear it.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { readAccessChanges, subscribeAccessRevision } from '@/lib/access-revision';

/** How many times the viewer's access has changed — grows, never resets. */
export function useAccessChanges(): number {
  return useSyncExternalStore(subscribeAccessRevision, readAccessChanges, () => 0);
}

/** Run `onChange` after each change of the viewer's access (not on mount). */
export function useAccessChange(onChange: () => void): void {
  const changes = useAccessChanges();
  const previous = useRef(changes);
  const latest = useRef(onChange);
  latest.current = onChange;
  useEffect(() => {
    if (previous.current === changes) return;
    previous.current = changes;
    latest.current();
  }, [changes]);
}

/**
 * Was `selection` made before the latest change of the viewer's access?
 * Stamped with the change count whenever the selection itself changes, so a
 * selection made after the change (or cleared and made again) is current.
 * `null`/`undefined` is never stale — nothing is selected.
 */
export function useStaleAfterAccessChange(selection: unknown): boolean {
  const changes = useAccessChanges();
  const selectionRef = useRef(selection);
  const stampRef = useRef(changes);
  if (selection !== selectionRef.current) {
    selectionRef.current = selection;
    stampRef.current = changes;
  }
  return selection !== null && selection !== undefined && stampRef.current !== changes;
}
